<template>
    <div>
        <v-col cols="12">
            <b-alert
                v-if="alertType"
                :type="alertType"
                :message="notificationMessage"
                :has-header-button="showAssignButton"
                :loading="horibaSamplesLoading"
                :button-title="assignButtonText"
                @emitAction="assignToProvider"
            ></b-alert>
            <button class="button back-to-list" @click="backToList">
                <v-icon>mdi-arrow-left</v-icon>
                <span class="text">Back To List</span>
            </button>
            <template v-if="dateTableMode === 'group'">
                <horiba-samples-group-data-table :list-route="listRoute" :initial-filters="initialFilters">
                </horiba-samples-group-data-table>
            </template>
            <template v-if="dateTableMode === 'single'">
                <horiba-samples-data-table :list-route="listRoute" :initial-filters="initialFilters">
                </horiba-samples-data-table>
            </template>
        </v-col>
    </div>
</template>

<script>
export default {
    props: {
        listRoute: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            alertType: null,
            providerId: null,
            createdAt: null,
            initialFilters: {},
        };
    },
    computed: {
        horibaSamples() {
            return this.$store.state.qualityControl.horiba_samples;
        },
        horibaSamplesLoading() {
            return this.horibaSamples.loading;
        },
        horibaSamplesProvider() {
            return this.horibaSamples.provider;
        },
        horibaSamplesCurrentUserProviders() {
            return this.horibaSamples.currentUserDevices;
        },
        showAssignButton() {
            const access = this.can('api.v1.providers.assign');
            const deviceOperator =
                this.horibaSamplesProvider.operator && this.horibaSamplesProvider.operator.id
                    ? this.horibaSamplesProvider.operator.id
                    : false;
            const assignToCurrentUser = deviceOperator === this.userId;
            const currentUserDevices = this.horibaSamplesCurrentUserProviders;
            const qcMode = !this.horibaSamplesProvider.is_patient_mode;

            if (!access) {
                return false;
            }
            if (currentUserDevices.length > 0) {
                return false;
            }
            if (assignToCurrentUser) {
                return false;
            }
            //case CurrentUser has no Providers and Device have no Operator
            if (!deviceOperator) {
                return true;
            }
            //case Device has Operator in QCMode
            if (qcMode && deviceOperator) {
                return true;
            }
            //case Device has Operator in PaticntMode
            if (!qcMode && deviceOperator) {
                return false;
            }
            return false;
        },
        assignButtonText() {
            const access = this.can('api.v1.providers.assign');
            const deviceOperator =
                this.horibaSamplesProvider.operator && this.horibaSamplesProvider.operator.id
                    ? this.horibaSamplesProvider.operator.id
                    : false;
            const assignToCurrentUser = deviceOperator === this.userId;
            const currentUserDevices = this.horibaSamplesCurrentUserProviders;
            const qcMode = !this.horibaSamplesProvider.is_patient_mode;

            if (!access) {
                return '';
            }
            if (currentUserDevices.length > 0) {
                return '';
            }
            if (assignToCurrentUser) {
                return '';
            }
            //case CurrentUser has no Providers and Device have no Operator
            if (!deviceOperator) {
                return t('quality-control::button.assign_to_me');
            }
            //case Device has Operator in QCMode
            if (qcMode && deviceOperator) {
                return t('quality-control::button.re_assign_to_me');
            }
            //case Device has Operator in PaticntMode
            if (!qcMode && deviceOperator) {
                return '';
            }
            return false;
        },
        notificationMessage() {
            if (!this.horibaSamplesProvider) {
                return;
            }
            const operatorName =
                this.horibaSamplesProvider.operator && this.horibaSamplesProvider.operator.name
                    ? this.horibaSamplesProvider.operator.name
                    : false;
            // device without operator
            if (!operatorName) {
                return `${this.horibaSamplesProvider.name ? this.horibaSamplesProvider.name : ''} ${t(
                    'quality-control::notification.blocked_for_patient_samples'
                )} (${t('quality-control::notification.no_user_linked_yet')}) ${moment(new Date()).format(
                    this.$root.dateFormat
                )}`;
            }

            if (this.horibaSamplesProvider.is_patient_mode) {
                // Patient mode
                return (
                    `${this.horibaSamplesProvider.name ? this.horibaSamplesProvider.name : ''} ` +
                    t('quality-control::notification.is_currently_managed_by') +
                    ` (${this.horibaSamplesProvider.operator.name ? this.horibaSamplesProvider.operator.name : ''})` +
                    ` ${moment(new Date()).format(this.$root.dateFormat)}`
                );
            }

            // QC mode
            return (
                `${this.horibaSamplesProvider.name ? this.horibaSamplesProvider.name : ''} ` +
                t('quality-control::notification.blocked_for_patient_samples') +
                ` (${this.horibaSamplesProvider.operator.name ? this.horibaSamplesProvider.operator.name : ''})` +
                ` ${moment(new Date()).format(this.$root.dateFormat)}`
            );
        },
        userId() {
            let userData = User || null;
            if (!userData) {
                return null;
            }
            return User.id;
        },
        dateTableMode() {
            if (!!this.providerId && !this.createdAt) {
                return 'group';
            }
            if (!!this.providerId && !!this.createdAt) {
                return 'single';
            }

            return 'group';
        },
    },
    watch: {
        horibaSamplesProvider(provider) {
            this.alertType = provider && provider.is_patient_mode ? 'info' : 'error';
        },
    },
    created() {
        this.$store.dispatch('qualityControl/fetchHoribaSamplesCurrentUserProviders', this.userId);
        this.setParamsFromQueryParams();
        this.horibaSamplesCurrentUserProviders;
    },
    methods: {
        loadProviderInfo() {
            return this.$store.dispatch('qualityControl/fetchHoribaSamplesProvider', this.providerId).catch(() => {
                this.$root.goTo(route('quality-control.index'));
            });
        },
        backToList() {
            if (this.dateTableMode === 'single') {
                return window.history.back();
            }
            return this.$root.goTo(route('quality-control.index'));
        },
        setParamsFromQueryParams() {
            let urlParams = new URLSearchParams(window.location.search);
            if (urlParams.has('provider_id')) {
                this.providerId = urlParams.get('provider_id');
                this.initialFilters.provider_id = this.providerId;
                if (this.can('api.v1.providers.show')) {
                    this.loadProviderInfo();
                }
            }
            if (urlParams.has('created_at')) {
                this.createdAt = urlParams.get('created_at');
                this.initialFilters.created_at = this.createdAt;
            }
        },
        assignToProvider() {
            axios({
                method: 'put',
                url: route('api.v1.providers.assign', { blood_result_provider: this.providerId }),
            })
                .then((success) => {
                    this.$store.dispatch('qualityControl/fetchHoribaSamplesCurrentUserProviders', this.userId);
                    this.$root.notify('info', success.data.operator);
                    if (success.data.neqas_messages) {
                        _.delay(() => {
                            this.$root.notify(
                                'warning',
                                t('default::notification.success'),
                                `${Object.values(success.data.neqas_messages).join('<br/>')}`
                            );
                        }, 300);
                    }
                })
                .catch((error) => {
                    this.$root.notify('error', error.response.data.error);
                })
                .finally(() => {
                    this.datatableKey++;
                    this.$store.dispatch('qualityControl/fetchBlockedDeviceList');
                    this.loadProviderInfo();
                });
        },
        exportCsvUrl() {
            // return route('horiba.download', {
            //     file: providerId,
            // });
        },
    },
};
</script>

<style></style>
